import React, { useState, useEffect } from 'react';
import { Plus, Edit3, ToggleLeft, ToggleRight, RefreshCw, KeyRound, X, Check } from 'lucide-react';
import api from '../../services/api';

interface XPPartner {
  id: number;
  name: string;
  category: string;
  experience_title: string;
  benefit_description: string;
  location_description: string;
  google_maps_url: string;
  contact_number: string;
  cover_image_url: string;
  default_discount_percentage: number | null;
  default_redemption_policy: string;
  default_redemption_limit: number | null;
  is_active: boolean;
  vendor_pin_last_updated: string | null;
  created_at: string;
  latitude: number | null;
  longitude: number | null;
}

const CATEGORIES = [
  'Restaurant', 'Spa', 'Watersports', 'Fashion / Swing Dress',
  'Café', 'Beach Club', 'Wellness', 'Attractions', 'Shopping', 'Transport',
];

const POLICIES = ['ONCE', 'ONCE_PER_DAY', 'PER_TRIP_LIMIT', 'UNLIMITED'];

const POLICY_LABELS: Record<string, string> = {
  ONCE: 'Once per trip',
  ONCE_PER_DAY: 'Once per day',
  PER_TRIP_LIMIT: 'Limited uses',
  UNLIMITED: 'Unlimited',
};

const emptyForm = {
  name: '',
  category: 'Restaurant',
  experience_title: '',
  benefit_description: '',
  location_description: '',
  google_maps_url: '',
  contact_number: '',
  cover_image_url: '',
  default_discount_percentage: '',
  default_redemption_policy: 'ONCE',
  default_redemption_limit: '',
  vendor_pin: '',
  latitude: '',
  longitude: '',
};

const XPPartnersPage: React.FC = () => {
  const [partners, setPartners] = useState<XPPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [pinResetId, setPinResetId] = useState<number | null>(null);
  const [newPin, setNewPin] = useState('');
  const [resettingPin, setResettingPin] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/xp/partners');
      if (res.data.success) setPartners(res.data.data);
    } catch (e) {
      console.error('Error loading XP partners:', e);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (p: XPPartner) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      category: p.category,
      experience_title: p.experience_title || '',
      benefit_description: p.benefit_description || '',
      location_description: p.location_description || '',
      google_maps_url: p.google_maps_url || '',
      contact_number: p.contact_number || '',
      cover_image_url: p.cover_image_url || '',
      default_discount_percentage: p.default_discount_percentage?.toString() || '',
      default_redemption_policy: p.default_redemption_policy,
      default_redemption_limit: p.default_redemption_limit?.toString() || '',
      vendor_pin: '',
      latitude: p.latitude?.toString() || '',
      longitude: p.longitude?.toString() || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name || !form.category || !form.default_redemption_policy) {
      alert('Name, category and redemption policy are required.');
      return;
    }
    if (!editingId && !form.vendor_pin) {
      alert('Partner PIN is required when creating a new partner.');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        name: form.name,
        category: form.category,
        experience_title: form.experience_title || null,
        benefit_description: form.benefit_description || null,
        location_description: form.location_description || null,
        google_maps_url: form.google_maps_url || null,
        contact_number: form.contact_number || null,
        cover_image_url: form.cover_image_url || null,
        default_discount_percentage: form.default_discount_percentage ? parseFloat(form.default_discount_percentage) : null,
        default_redemption_policy: form.default_redemption_policy,
        default_redemption_limit: form.default_redemption_limit ? parseInt(form.default_redemption_limit) : null,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
      };
      if (form.vendor_pin) payload.vendor_pin = form.vendor_pin;

      if (editingId) {
        await api.put(`/xp/partners/${editingId}`, payload);
      } else {
        await api.post('/xp/partners', payload);
      }
      setShowForm(false);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Error saving partner');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p: XPPartner) => {
    try {
      await api.put(`/xp/partners/${p.id}`, { is_active: !p.is_active });
      load();
    } catch (e) {
      console.error('Error toggling partner:', e);
    }
  };

  const resetPin = async (id: number) => {
    if (!newPin || newPin.length < 4) {
      alert('PIN must be at least 4 digits.');
      return;
    }
    setResettingPin(true);
    try {
      await api.post(`/xp/partners/${id}/reset-pin`, { vendor_pin: newPin });
      setPinResetId(null);
      setNewPin('');
      alert('Partner PIN updated successfully.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Error resetting PIN');
    } finally {
      setResettingPin(false);
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Loading XP Partners...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">✨ XP Partners</h1>
          <p className="text-sm text-gray-500 mt-1">Global partner library for XP by Balify</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" /> Add Partner
          </button>
        </div>
      </div>

      {/* How it works banner */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 mb-6 flex items-start gap-3">
        <span className="text-indigo-500 text-lg mt-0.5">ℹ️</span>
        <div className="text-sm text-indigo-800">
          <span className="font-semibold">This is the global partner library.</span> Partners added here are available to assign to any booking.
          {' '}To give a guest their privileges, go to <span className="font-semibold">Booking Dashboard → open a booking → XP tab</span> and assign partners from this library.
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">{partners.length}</div>
          <div className="text-sm text-gray-500">Total Partners</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-green-600">{partners.filter(p => p.is_active).length}</div>
          <div className="text-sm text-gray-500">Active</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-400">{partners.filter(p => !p.is_active).length}</div>
          <div className="text-sm text-gray-500">Inactive</div>
        </div>
      </div>

      {/* Partners Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full min-w-[900px]">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Benefit</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Policy</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PIN Updated</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {partners.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">
                  No XP partners yet. Add your first partner to get started.
                </td>
              </tr>
            ) : (
              partners.map(p => (
                <tr key={p.id} className={`hover:bg-gray-50 ${!p.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 text-sm">{p.name}</div>
                    {p.location_description && (
                      <div className="text-xs text-gray-400">📍 {p.location_description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded-full font-medium">
                      {p.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-700">{p.benefit_description || '—'}</div>
                    {p.default_discount_percentage && (
                      <div className="text-xs text-green-600 font-medium">{p.default_discount_percentage}% off</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      p.default_redemption_policy === 'UNLIMITED' ? 'bg-green-100 text-green-700' :
                      p.default_redemption_policy === 'ONCE' ? 'bg-blue-100 text-blue-700' :
                      p.default_redemption_policy === 'ONCE_PER_DAY' ? 'bg-purple-100 text-purple-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {POLICY_LABELS[p.default_redemption_policy]}
                      {p.default_redemption_policy === 'PER_TRIP_LIMIT' && p.default_redemption_limit ? ` (${p.default_redemption_limit}x)` : ''}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatDate(p.vendor_pin_last_updated)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      p.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {p.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(p)}
                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                        title="Edit"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { setPinResetId(p.id); setNewPin(''); }}
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                        title="Reset PIN"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleActive(p)}
                        className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                        title={p.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {p.is_active
                          ? <ToggleRight className="w-4 h-4 text-green-500" />
                          : <ToggleLeft className="w-4 h-4" />
                        }
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Edit XP Partner' : 'Add XP Partner'}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Partner Name *</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Seminyak Spa & Wellness"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  >
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Experience Title</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.experience_title}
                    onChange={e => setForm(f => ({ ...f, experience_title: e.target.value }))}
                    placeholder="e.g. Exclusive Wellness Experience"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Benefit Description</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.benefit_description}
                    onChange={e => setForm(f => ({ ...f, benefit_description: e.target.value }))}
                    placeholder="e.g. 25% OFF Spa Treatment"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.location_description}
                    onChange={e => setForm(f => ({ ...f, location_description: e.target.value }))}
                    placeholder="e.g. Seminyak, Bali"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Contact Number</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.contact_number}
                    onChange={e => setForm(f => ({ ...f, contact_number: e.target.value }))}
                    placeholder="+62 ..."
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Google Maps URL</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.google_maps_url}
                    onChange={e => setForm(f => ({ ...f, google_maps_url: e.target.value }))}
                    placeholder="https://maps.google.com/..."
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Cover Image URL</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.cover_image_url}
                    onChange={e => setForm(f => ({ ...f, cover_image_url: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Default Discount %</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.default_discount_percentage}
                    onChange={e => setForm(f => ({ ...f, default_discount_percentage: e.target.value }))}
                    placeholder="e.g. 25"
                    min="0" max="100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Redemption Policy *</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.default_redemption_policy}
                    onChange={e => setForm(f => ({ ...f, default_redemption_policy: e.target.value }))}
                  >
                    {POLICIES.map(p => <option key={p} value={p}>{POLICY_LABELS[p]}</option>)}
                  </select>
                </div>
                {form.default_redemption_policy === 'PER_TRIP_LIMIT' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Redemption Limit</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={form.default_redemption_limit}
                      onChange={e => setForm(f => ({ ...f, default_redemption_limit: e.target.value }))}
                      placeholder="e.g. 3"
                      min="1"
                    />
                  </div>
                )}
                <div className={form.default_redemption_policy === 'PER_TRIP_LIMIT' ? '' : 'col-span-2'}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Partner PIN {editingId ? '(leave blank to keep current)' : '*'}
                  </label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={form.vendor_pin}
                    onChange={e => setForm(f => ({ ...f, vendor_pin: e.target.value }))}
                    placeholder="4+ digit PIN"
                  />
                </div>
                {/* Coordinates */}
                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-gray-700">Coordinates (for Near You feature)</label>
                    <button
                      type="button"
                      onClick={() => {
                        const url = form.google_maps_url;
                        if (!url) { alert('Enter a Google Maps URL first.'); return; }
                        // Extract from @lat,lng in full maps URL
                        const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
                        // Extract from 3d/4d pattern (place pin coordinates)
                        const pinMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
                        if (pinMatch) {
                          setForm(f => ({ ...f, latitude: pinMatch[1], longitude: pinMatch[2] }));
                        } else if (atMatch) {
                          setForm(f => ({ ...f, latitude: atMatch[1], longitude: atMatch[2] }));
                        } else {
                          alert('Could not extract coordinates. Paste the full Google Maps URL (not a short link).');
                        }
                      }}
                      className="text-xs text-indigo-600 font-medium hover:text-indigo-800"
                    >
                      📍 Extract from Maps URL
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={form.latitude}
                      onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))}
                      placeholder="Latitude e.g. -8.7175502"
                    />
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={form.longitude}
                      onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))}
                      placeholder="Longitude e.g. 115.2157215"
                    />
                  </div>
                  {form.latitude && form.longitude && (
                    <a
                      href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-indigo-500 mt-1 inline-block hover:underline"
                    >
                      ✓ Verify on Google Maps →
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-200">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Partner'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset PIN Modal */}
      {pinResetId !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Reset Partner PIN</h2>
            <p className="text-sm text-gray-500 mb-4">
              Enter a new PIN for this partner. Share it with the vendor directly.
            </p>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 mb-4"
              value={newPin}
              onChange={e => setNewPin(e.target.value)}
              placeholder="New PIN (4+ digits)"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setPinResetId(null); setNewPin(''); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => resetPin(pinResetId)}
                disabled={resettingPin}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {resettingPin ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {resettingPin ? 'Saving...' : 'Update PIN'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default XPPartnersPage;
